import { useEffect, useState } from 'react';
import { CircularProgress } from '@mui/material';

// AI-CONTEXT: See docs/features/livereport-widgets-dashboard-parity.md
// Lightweight renderers shared by `BaseControl` and the editor panel. They
// live here (instead of `editor/WidgetControl.jsx`) so `controls/index.js`
// can import them without dragging in the widgets layer — which would
// create a circular dependency (controls → editor → widgets → controls).

/**
 * @param {object} props
 * @param {string} props.label - Label for the grouped control
 * @param {boolean} [props.isOpen=false] - Whether the accordion is initially open
 * @param {any} props.children - Child components
 */
export const ControlsAccordionMenu = ({
    isOpen = false,
    label,
    children,
}) => {

    const [_isOpen, setIsOpen] = useState(isOpen);

    return (
        <div className="report-builder-control-group">
            <button
                className="report-builder-control-group-toggle"
                onClick={() => setIsOpen(open => !open)}
                aria-expanded={_isOpen}
            >
                <span>{label}</span>
                <span
                    className="report-builder-control-group-arrow"
                    aria-hidden="true"
                    style={{
                        transform: `rotate(${_isOpen ? 180 : 0} deg)`,
                    }}
                >
                    ▾
                </span>
            </button>
            {_isOpen && (
                <div className="report-builder-control-group-content">
                    {children}
                </div>
            )}
        </div>
    )
}

/**
 * Loads a control's async options before rendering it: a spinner while
 * loading, an alert naming the control when the load fails. Used for
 * top-level controls (WidgetControl's ControlRenderer) and for the children
 * of a grouped control alike — a grouped child with async options otherwise
 * never loads and renders with its options still a function.
 * BaseControl.load() caches one in-flight promise, so mounting twice reads once.
 *
 * @param {object} props
 * @param {import('.').BaseControl} props.control - Control to load
 * @param {() => React.ReactNode} props.children - Renders the control once its options are loaded
 */
export const ControlLoadGate = ({ control, children }) => {

    const [loadState, setLoadState] = useState(
        /** @type {'loading' | 'loaded' | 'error'} */ (control.loaded ? 'loaded' : 'loading')
    );

    useEffect(() => {
        if (control.loaded) {
            setLoadState('loaded');
            return undefined;
        }
        let active = true;
        setLoadState('loading');
        control.load().then(
            () => { if (active) setLoadState('loaded'); },
            (error) => {
                console.error(`WidgetControl: options for '${control.label}' failed to load`, error);
                if (active) setLoadState('error');
            }
        );
        return () => { active = false; };
    }, [control]);

    if (loadState === 'error') {
        return (
            <p role="alert" className="widget-control-load-error">
                De opties voor '{control.label}' konden niet worden geladen. Herlaad de pagina om het opnieuw te proberen.
            </p>
        );
    }

    /* Read the control itself, not only state: a new control instance is
       unloaded while state still says 'loaded' from the previous one. */
    if (!control.loaded) {
        return <CircularProgress size={20} />;
    }

    return children();
};

/**
 * Renders a grouped control with children in an accordion section.
 *
 * @param {object} props
 * @param {string} props.label - Label for the grouped control
 * @param {any} props.value - Current value of the grouped control
 * @param {(value: any) => void} props.onChange - Callback for changes in the grouped control
 * @param {Object.<string, any>} props.controls - Nested controls
 * @param {import('../widgets').BaseWidget} [props.widget] - Owning widget, forwarded to child visibleWhen predicates
 */
export const GroupedControlRenderer = ({
    label,
    value,
    onChange,
    controls,
    widget,
}) => {
    /* Parent value can be undefined when a saved widget pre-dates this
       grouped control — fall back to {} so child reads don't crash. */
    const safeValue = value ?? {};
    return (
        <ControlsAccordionMenu label={label}>
            {Object.entries(controls).map(([key, child]) => {
                /* Same visibility guard as top-level controls (WidgetControl's
                   ControlRenderer), extended with the group's value so a child
                   can depend on sibling settings within the group. */
                if (typeof child.visibleWhen === 'function'
                    && !child.visibleWhen(widget, safeValue)) {
                    return null;
                }
                return (
                    <ControlLoadGate key={key} control={child}>
                        {() => child.render(
                            safeValue[key],
                            (newValue) => onChange(
                                { ...safeValue, [key]: newValue }
                            ))
                        }
                    </ControlLoadGate>
                );
            })}
        </ControlsAccordionMenu>
    );
}
